import { useCallback, useEffect, useState } from 'react'
import { api, type HangmanGame } from '../api'
import { radius, theme } from '../theme'
import { Button, Card, Page } from '../components/ui'
import { ErrorScreen, Loading, Empty } from '../components/States'
import { LetterKeyboard, type KeyMark } from '../components/LetterKeyboard'
import { Mascot } from '../components/Mascot'
import { Trace } from '../lib/trace'
import { say } from '../lib/speech'
import { sfx } from '../lib/sound'
import { BackRow } from './MysteryWord'

/**
 * Hangman: find one of your own words a letter at a time (Sam, 18 Oct 2026).
 *
 * The classic game, with the gallows swapped for a flower: each letter that
 * is not in the word drops a petal, and the flower has seven. Same rules as
 * the other games (D-063): no score, no streak, nothing saved, and the word,
 * its meaning and its sentence are shown at the end whichever way it went.
 *
 * The meaning is always on screen. Hangman without a clue is luck with
 * letters; with the meaning, finding the word is pulling it out of memory.
 */
const PETAL_COLORS = ['#FF8A80', '#FFB74D', '#FFD54F', '#AED581', '#4FC3F7', '#9575CD', '#F06292']

export function Flower({ left, total }: { left: number; total: number }) {
  const cx = 80
  const cy = 70
  return (
    <svg viewBox="0 0 160 170" role="img" aria-label={`${left} of ${total} petals left`}
      style={{ width: 150, height: 160, display: 'block', margin: '0 auto' }}>
      <path d="M80 100 C78 125 82 140 80 165" stroke="#4E8B3A" strokeWidth={6} fill="none" strokeLinecap="round" />
      <ellipse cx={62} cy={138} rx={16} ry={7} fill="#5FA052" transform="rotate(-30 62 138)" />
      <ellipse cx={98} cy={128} rx={16} ry={7} fill="#3F7D3A" transform="rotate(30 98 128)" />
      {Array.from({ length: total }, (_, i) => {
        const angle = (360 / total) * i - 90
        const gone = i >= left
        return (
          <g key={i} transform={`rotate(${angle} ${cx} ${cy})`}>
            <ellipse cx={cx + 30} cy={cy} rx={22} ry={12} fill={PETAL_COLORS[i % PETAL_COLORS.length]}
              stroke="#5A3D2B" strokeWidth={1.5}
              style={{ transition: 'opacity 0.5s ease-in', opacity: gone ? 0 : 1 }} />
          </g>
        )
      })}
      <circle cx={cx} cy={cy} r={15} fill="#8D5A2B" stroke="#5A3D2B" strokeWidth={2} />
      <circle cx={cx - 4} cy={cy - 4} r={4} fill="#B07A45" />
    </svg>
  )
}

export function Hangman({
  learnerId, onBack, onHome,
}: { learnerId: string; onBack: () => void; onHome: () => void }) {
  const [game, setGame] = useState<HangmanGame | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [tried, setTried] = useState<string[]>([])

  const load = useCallback(() => {
    setError(null)
    setGame(null)
    setTried([])
    api.hangman(learnerId).then(setGame).catch(setError)
  }, [learnerId])
  useEffect(load, [load])

  const ready = game && game.ready ? game : null
  const misses = ready ? tried.filter((l) => !ready.answer.includes(l)).length : 0
  const won = Boolean(ready && [...ready.answer].every((l) => tried.includes(l)))
  const lost = Boolean(ready && !won && misses >= ready.misses)
  const over = won || lost

  useEffect(() => {
    if (!ready || !over) return
    if (won) { sfx.correct(); window.setTimeout(() => sfx.finished(), 450) }
    else sfx.notYet()
    window.setTimeout(() => say(ready.word), 600)
  }, [over, won, ready])

  const onLetter = useCallback((l: string) => {
    if (!ready || over || tried.includes(l)) return
    if (ready.answer.includes(l)) sfx.tap()
    setTried((t) => [...t, l])
  }, [ready, over, tried])

  if (error) return <ErrorScreen error={error} onRetry={load} onHome={onHome} />
  if (!game) return <Loading what="Thinking of a word" />
  if (!ready) {
    return (
      <Page width={520}>
        <BackRow onBack={onBack} />
        <Empty emoji="🌼" title="Not yet" line={(game as { message: string }).message} />
      </Page>
    )
  }

  const marks: Record<string, KeyMark> = {}
  for (const l of tried) marks[l] = ready.answer.includes(l) ? 'right' : 'out'
  const left = ready.misses - misses

  return (
    <Page width={520}>
      <BackRow onBack={onBack} />
      <h1 style={{ margin: '4px 0 2px', fontSize: 28, color: theme.text }}>Hangman</h1>
      <p style={{ margin: '0 0 8px', fontSize: 16, color: theme.muted }}>
        One of your words, with {ready.length} letters. Each letter that is not in it drops a petal.
      </p>

      <Flower left={left} total={ready.misses} />

      {/* the word, one box per letter */}
      <div aria-label="The word so far" style={{
        display: 'flex', gap: 6, justifyContent: 'center', flexWrap: 'wrap', margin: '6px 0 14px',
      }}>
        {[...ready.answer].map((l, i) => {
          const shown = tried.includes(l) || over
          const missed = over && !tried.includes(l)
          return (
            <div key={i} aria-label={shown ? l : 'hidden letter'} style={{
              width: 'clamp(28px, 8vw, 40px)', height: 'clamp(38px, 10vw, 50px)',
              display: 'grid', placeItems: 'center',
              borderBottom: `4px solid ${theme.text2}`,
              fontSize: 'clamp(22px, 6vw, 30px)', fontWeight: 800,
              color: missed ? theme.orangeDark : theme.text,
            }}>{shown ? l : ''}</div>
          )
        })}
      </div>

      {over ? (
        <Card style={{ textAlign: 'center', marginBottom: 14 }}>
          <div style={{ display: 'grid', placeItems: 'center', marginBottom: 6 }}>
            <Mascot size={84} mood={won ? 'cheer' : 'wonder'} />
          </div>
          <div style={{ fontSize: 17, color: theme.muted, fontWeight: 700 }}>
            {won ? 'You found it!' : 'The word was'}
          </div>
          <button
            type="button"
            onClick={() => say(ready.word)}
            aria-label={`Hear ${ready.word}`}
            style={{
              margin: '4px 0 6px', fontSize: 34, fontWeight: 800, color: theme.greenDarker,
              background: 'transparent', border: 'none', cursor: 'pointer', fontFamily: 'inherit',
            }}
          >
            <Trace id="T3.display_form">{ready.word}</Trace> 🔊
          </button>
          {ready.full_sentence ? (
            <p style={{ margin: '0 0 14px', fontSize: 15, color: theme.muted, fontStyle: 'italic' }}>
              <Trace id="T4.example_sentence">“{ready.full_sentence}”</Trace>
            </p>
          ) : null}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button tone="child" onClick={load}>Another word</Button>
            <Button tone="ghost" onClick={onBack}>Other games</Button>
          </div>
        </Card>
      ) : null}

      <div style={{
        background: theme.greenPale, borderRadius: radius.lg, padding: '10px 14px', marginBottom: 12,
      }}>
        <p style={{ margin: '2px 0', fontSize: 16, color: theme.text2, lineHeight: 1.45 }}>
          <strong>Meaning:</strong>{' '}
          <Trace id="T4.definition">{ready.definition}</Trace>{' '}
          <button type="button" onClick={() => say(ready.definition)} aria-label="Read the meaning out loud"
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 16 }}>🔊</button>
        </p>
        {!over && ready.sentence && misses >= 3 ? (
          <p style={{ margin: '6px 0 2px', fontSize: 15, color: theme.muted, fontStyle: 'italic' }}>
            <Trace id="T4.example_sentence">“{ready.sentence}”</Trace>
          </p>
        ) : null}
      </div>

      {!over ? <LetterKeyboard onLetter={onLetter} marks={marks} /> : null}
    </Page>
  )
}
